import { faCalendarLinesPen, faMessageDots, IconDefinition, faGlobe, faSignalSlash, faEnvelope, faUserPlus, faClock, faComment, faUserLargeSlash, faGraduationCap, faListCheck } from "@fortawesome/pro-solid-svg-icons";
import { QueryClient, useMutation, useQuery } from "@tanstack/react-query";

import clientSDK from "../../App/ClientSDK";

const baseUrl = 'reports/establishment-stats'
const anonymousBaseUrl = 'anonymous/establishment-stats';

type QueryFilters = {
    starts_at: string;
    ends_at: string;
};

type ShareUrl = {
    long: string;
    short: string;
};

type EstablishmentStats = {
    total_appointments_booked: number | null;
    total_online_appointments_booked: number | null;
    total_offline_appointments_booked: number | null;
    total_emails_sent: number | null;
    total_text_messages_sent: number | null;
    total_new_customers: number | null;
    total_last_minutes_booked: number | null;
    total_positive_feedbacks_received: number | null;
    total_no_shows: number | null;
    total_workshops_given: number | null;
    total_tasks_created: number | null;
};

type EstablishmentStatsResponse = {
    _meta: {
        query_filters: QueryFilters;
        share_url: ShareUrl
    };

    _resource: EstablishmentStats;
};


const getEstablishmentStats = async () => {
  const response = await clientSDK
    .instance()
    .get<EstablishmentStatsResponse>(baseUrl)
    const data = response.data
    return data
}

export const useGetEstablishmentStatsQuery = () => {
    return useQuery({
        queryKey: ['establishment-stats'],
        queryFn: getEstablishmentStats,
    })
}

type EstablishmentStatsPermissions = {
    is_total_appointments_booked_public: boolean;
    is_total_online_appointments_booked_public: boolean;
    is_total_offline_appointments_booked_public: boolean;
    is_total_emails_sent_public: boolean;
    is_total_text_messages_sent_public: boolean;
    is_total_new_customers_public: boolean;
    is_total_last_minutes_booked_public: boolean;
    is_total_positive_feedbacks_received_public: boolean;
    is_total_no_shows_public: boolean;
};

const getEstablishmentStatsPermissions = async () => {
  const response = await clientSDK
    .instance()
    .get<EstablishmentStatsPermissions>(`${baseUrl}/permissions`);
  const data = response.data;
  return data;
}

export const useGetEstablishmentStatsPermissionsQuery = () => {
    return useQuery({
        queryKey: ['establishment-stats-permissions'],
        queryFn: getEstablishmentStatsPermissions,
    });
}


const patchEstablishmentStatsPermissions = async (updateObject: {
    stat_key: string;
    is_public: boolean;
  }) => {
    const response = await clientSDK
      .instance()
      .patch(`${baseUrl}/permissions`, updateObject);
    return response.data;
  };
  
  export const usePatchEstablishmentStatsPermissionsMutation = (queryClient: QueryClient) => {
    return useMutation({
      mutationFn: patchEstablishmentStatsPermissions,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ['establishment-stats-permissions'] });
      },
      onMutate: async (newPermissions) => {
        await queryClient.cancelQueries({ queryKey: ['establishment-stats-permissions'] })
        const previousPermissions = queryClient.getQueryData(['establishment-stats-permissions'])
        queryClient.setQueryData(['establishment-stats-permissions'], (old: EstablishmentStatsPermissions) => ({...old, [newPermissions.stat_key]: newPermissions.is_public}))
    return { previousPermissions }
      },
    });
  };

export type TransformedStatsData = {
    value: number | null;
    isPublic: boolean;
    permissionKey: string;
    key: string;
    meta?: MetaDataForKeys
};

type MetaDataForKeys = {
    FaIcon?: IconDefinition
    color?: string
    note?: string
    title?: string
    percentageVersus?: string
}

const metaDataMapping: Partial<{ [key in keyof EstablishmentStats]: MetaDataForKeys }> = {
    total_appointments_booked: { 
        FaIcon: faCalendarLinesPen,
        color: '#FF0000', // Red
        title: 'Total Appointments Booked' 
    },
    total_text_messages_sent: { 
        FaIcon: faMessageDots,
        color: '#FFA500', // Orange
        title: 'Total Text Messages Sent',
        note: 'Stats since 20-05-2024',
    },
    total_online_appointments_booked: { 
        FaIcon: faGlobe,
        color: '#FFFF00', // Yellow
        title: 'Online Appointments Booked', 
        percentageVersus: 'total_appointments_booked'
    },
    total_offline_appointments_booked: { 
        FaIcon: faSignalSlash,
        color: '#008000', // Green
        title: 'Offline Appointments Booked', 
        percentageVersus: 'total_appointments_booked'
    },
    total_emails_sent: { 
        FaIcon: faEnvelope,
        color: '#0000FF', // Blue
        title: 'Total Emails Sent' 
    },
    total_new_customers: { 
        FaIcon: faUserPlus,
        color: '#4B0082', // Indigo
        title: 'Total New Customers' 
    },
    total_last_minutes_booked: { 
        FaIcon: faClock,
        color: '#EE82EE', // Violet
        title: 'Total Last Minutes Booked',
    },
    total_positive_feedbacks_received: { 
        FaIcon: faComment,
        color: '#00FFFF', // Cyan
        title: 'Total Positive Feedbacks Received' 
    },
    total_no_shows: { 
        FaIcon: faUserLargeSlash,
        color: '#FFC0CB', // Pink
        title: 'No Shows',
        percentageVersus: 'total_appointments_booked'
    },
    total_workshops_given: {
        FaIcon: faGraduationCap,
        color: '#FF0000', // Red
        title: 'Total Workshops Given'
    },
    total_tasks_created: {
        FaIcon: faListCheck,
        color: '#FFA500', // Orange
        title: 'Total Tasks Created'
    },
}


const getAnonymousEstablishmentStats = async (uuid: string) => {
    const response = await clientSDK
      .instance()
      .getAnonymous<EstablishmentStatsResponse>(`${anonymousBaseUrl}/${uuid}`);
    const data = response.data;
    return data;
  };
  
  export const useGetAnonymousEstablishmentStatsQuery = (uuid: string) => {
    return useQuery({
      queryKey: ['anonymous-establishment-stats', uuid],
      queryFn: () => getAnonymousEstablishmentStats(uuid),
    });
  };

export const  transformStatData = (stats: EstablishmentStats, permissions: EstablishmentStatsPermissions): TransformedStatsData[] => {
    const transformedData: TransformedStatsData[] = [];

    for (const key in stats) {
        if (stats.hasOwnProperty(key)) {
            const permissionKey = `is_${key}_public` as keyof EstablishmentStatsPermissions;
            transformedData.push({
                value: stats[key as keyof EstablishmentStats],
                isPublic: permissions[permissionKey],
                permissionKey,
                key,
                meta: metaDataMapping[key as keyof EstablishmentStats]
            });
        }
    }

    return transformedData;
}

export const transformPublicStatData = (stats: EstablishmentStats): TransformedStatsData[] => {
    const transformedData: TransformedStatsData[] = [];
    for (const key in stats) {
        if (stats.hasOwnProperty(key) && stats[key as keyof EstablishmentStats] !== null) {
            const permissionKey = `is_${key}_public` as keyof EstablishmentStatsPermissions;
            transformedData.push({
                value: stats[key as keyof EstablishmentStats],
                isPublic: true,
                permissionKey,
                key,
                meta: metaDataMapping[key as keyof EstablishmentStats]
            });
        }
    }
    return transformedData;
}

type BookingLocationMeta = {
    id: number;
    name: string;
    image: string | null;
    online_booking_enabled: boolean;
};

type OnlineBookingInfoRelevantResponse = {
    uuid: string;
    id: number;
    locations: BookingLocationMeta[];
    referral_url: string;
    language: string;
};

const getOnlineBookingInfo = async (establishmentUuid: string) => {
  const response = await clientSDK
    .instance()
    .getAnonymous<OnlineBookingInfoRelevantResponse>(`anonymous/online-booking-info?establishment_uuid=${establishmentUuid}`);
  const data = response.data;
  return data;
};

export const useGetOnlineBookingInfoQuery = (establishmentUuid: string) => {
    return useQuery({
      queryKey: ['online-booking-info', establishmentUuid],
      queryFn: () => getOnlineBookingInfo(establishmentUuid),
    });
  };


  type PersonResponse = {
    type: string;
    attributes: {
        is_manager: boolean;
    };
};

const getPersonInfo = async () => {
  const response = await clientSDK
    .instance()
    .get<{data:PersonResponse}>('person');
  const data = response.data;
  return data;
};

export const useGetPersonInfoQuery = () => {
    return useQuery({
      queryKey: ['person-info'],
      queryFn: getPersonInfo,
    });
};
